import React from 'react';
import { ComponentSkinProps } from '../../../../types/component-skin';

/**
 * Basic Login Skin
 * 기본 로그인 스킨 - 기존 LoginComponent의 UI를 그대로 유지
 */
const BasicLoginSkin: React.FC<ComponentSkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils,
  app,
  editor 
}) => {
  const { t, cx } = utils;
  const { 
    formData,
    validationErrors,
    loading,
    loginSuccess,
    loginError,
    theme,
    // 새로 추가된 데이터
    redirectUrl, // 로그인 성공 후 리다이렉트 경로 (LoginLogic에서 처리)
    backgroundType,
    backgroundUrl,
    title,
    buttonColor,
    titleColor,
    labelColor,
    inputTextColor,
    style,
    className,
    isEditor,
    isPreview
  } = data;
  
  const {
    handleChange,
    handleSubmit,
    handleSignupClick
  } = actions;
  
  // Theme colors
  const primaryColor = theme?.primary || buttonColor || "#007bff";
  const primaryDarkColor = theme?.primaryDark || shadeColor(primaryColor, -15);
  const secondaryColor = theme?.secondary || "#6c757d";
  
  // Utility function to darken color
  function shadeColor(color: string, percent: number): string {
    try {
      if (!color || !color.startsWith('#') || color.length !== 7) {
        return percent < 0 ? '#005bb9' : '#3399ff';
      }

      let R = parseInt(color.substring(1, 3), 16);
      let G = parseInt(color.substring(3, 5), 16);
      let B = parseInt(color.substring(5, 7), 16);

      R = Math.max(0, Math.min(255, R + Math.round(R * percent / 100)));
      G = Math.max(0, Math.min(255, G + Math.round(G * percent / 100)));
      B = Math.max(0, Math.min(255, B + Math.round(B * percent / 100)));

      const rr = R.toString(16).padStart(2, '0');
      const gg = G.toString(16).padStart(2, '0');
      const bb = B.toString(16).padStart(2, '0');

      return `#${rr}${gg}${bb}`;
    } catch (e) {
      console.error('색상 변환 오류:', e);
      return percent < 0 ? '#005bb9' : '#3399ff';
    }
  }
  
  // Background styles
  const shouldRemoveBackground = backgroundType === 'none' || !backgroundUrl;
  
  // 디버깅 로그 (필요시 활성화)
  // console.log('[BasicLoginSkin] 배경 설정:', {
  //   backgroundType,
  //   backgroundUrl,
  //   shouldRemoveBackground,
  //   style,
  //   defaultStyleWidth: style?.width || '100%',
  //   defaultStyleHeight: style?.height || '100vh'
  // });
  
  const defaultStyle = {
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
    // 중요한 속성들은 style이 덮어쓰지 못하도록 다시 설정
    width: style?.width || '100%',
    height: style?.height || '100vh',
    position: 'relative' as const
  } as React.CSSProperties;
  
  if (shouldRemoveBackground) {
    defaultStyle.background = 'none';
    defaultStyle.backgroundImage = 'none';
    defaultStyle.backgroundColor = 'transparent';
  } else if (backgroundType === 'image' && backgroundUrl) {
    defaultStyle.backgroundImage = `url(${backgroundUrl})`;
    defaultStyle.backgroundSize = 'cover';
    defaultStyle.backgroundPosition = 'center';
    defaultStyle.backgroundRepeat = 'no-repeat';
  }
  
  // Form styles
  const formContainerStyle = {
    backgroundColor: 'transparent',
    borderRadius: style.borderRadius || '8px',
    boxShadow: 'none',
    padding: style.padding || '30px',
    maxWidth: '500px',
    width: '90%',
    zIndex: 10,
    position: 'relative'
  } as React.CSSProperties;

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    width: '100%',
    backgroundColor: 'transparent'
  } as React.CSSProperties;

  const formRowStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%'
  } as React.CSSProperties;

  const inputStyle = {
    padding: '10px 12px',
    border: '1px solid rgba(206, 212, 218, 0.7)',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    transition: 'border-color 0.2s',
    color: inputTextColor,
    backgroundColor: 'rgba(255, 255, 255, 0.7)'
  };

  const errorInputStyle = {
    ...inputStyle,
    borderColor: 'rgba(220, 53, 69, 0.8)',
    backgroundColor: 'rgba(255, 248, 248, 0.8)'
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '6px',
    display: 'block',
    color: labelColor,
    textShadow: '0 0 4px rgba(255, 255, 255, 0.8)'
  } as React.CSSProperties;

  const buttonStyle = {
    padding: '12px 20px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 'bold',
    marginTop: '10px',
    width: '100%',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)'
  };

  const signupButtonStyle = {
    padding: '12px 20px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    marginTop: '15px',
    width: '100%',
    textAlign: 'center',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    display: 'block',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)'
  } as React.CSSProperties;

  const errorMessageStyle = {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px',
    textShadow: '0 0 4px rgba(255, 255, 255, 0.8)'
  };

  const loginErrorStyle = {
    color: '#dc3545',
    fontSize: '14px',
    marginTop: '15px',
    padding: '12px',
    backgroundColor: 'rgba(255, 248, 248, 0.8)',
    borderRadius: '4px',
    borderLeft: '4px solid #dc3545'
  };

  const loginSuccessStyle = {
    textAlign: 'center',
    color: 'green',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: 'rgba(248, 255, 248, 0.8)',
    borderRadius: '4px'
  } as React.CSSProperties;

  const titleStyle = {
    marginTop: 0,
    marginBottom: '20px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    color: titleColor,
    textShadow: '0 0 5px rgba(255, 255, 255, 0.8)'
  } as React.CSSProperties;

  const videoStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  } as React.CSSProperties;

  // Special handling for specific companies
  const companyId = app?.company?.id;
  const skinId = data?.withcookieData?.skin?.id;
  const showLogo = (companyId === 24 || companyId === 26 || companyId === 27 || skinId === 317);
  const logoUrl = data?.withcookieData?.skin?.androidAppIconImage;

  return (
    <div 
      className={cx('login-form', className, shouldRemoveBackground && 'no-background')} 
      style={defaultStyle}
      data-background-type={backgroundType}
      data-has-background={!shouldRemoveBackground}
      data-is-editor={isEditor}
      data-is-preview={isPreview}
    >
      {/* Background elements */}
      {backgroundType === 'video' && backgroundUrl && (
        <>
          <video style={videoStyle} autoPlay loop muted>
            <source src={backgroundUrl} type="video/mp4" />
            {t('브라우저가 비디오 태그를 지원하지 않습니다.')}
          </video>
          <div 
            className="background-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1
            }}
          />
        </>
      )}
      
      {backgroundType === 'image' && backgroundUrl && (
        <div 
          className="background-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1
          }}
        />
      )}
      
      {/* Login form */}
      <div style={formContainerStyle}>
        {!showLogo && (
          <h2 style={titleStyle}>{title}</h2>
        )}
        
        {showLogo && logoUrl && (
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <img 
              src={logoUrl} 
              alt="logo" 
              style={{maxWidth:'300px', objectFit: 'contain', margin:'0 auto'}} 
            />
          </div>
        )}
        
        {loginSuccess ? (
          <div style={loginSuccessStyle}>
            <p>{t('로그인 성공! 환영합니다.')}</p>
          </div>
        ) : (
          <form style={formStyle} onSubmit={handleSubmit}>
            <div style={formRowStyle}>
              <label htmlFor="user_id" style={labelStyle}>{t('아이디')}</label>
              <input 
                id="user_id" 
                type="text" 
                placeholder={t("아이디 입력 (2~20자)")}
                required 
                style={validationErrors?.user_id ? errorInputStyle : inputStyle}
                value={formData?.user_id || ''}
                onChange={handleChange}
                minLength={2}
                maxLength={20}
              />
              {validationErrors?.user_id && (
                <div style={errorMessageStyle}>
                  {validationErrors.user_id}
                </div>
              )}
            </div>

            <div style={formRowStyle}>
              <label htmlFor="password" style={labelStyle}>{t('비밀번호')}</label>
              <input 
                id="password" 
                type="password" 
                placeholder={t("비밀번호 입력")}
                required 
                style={validationErrors?.password ? errorInputStyle : inputStyle}
                value={formData?.password || ''}
                onChange={handleChange}
              />
              {validationErrors?.password && (
                <div style={errorMessageStyle}>
                  {validationErrors.password}
                </div>
              )}
            </div>

            {loginError && (
              <div style={loginErrorStyle}>
                {typeof loginError === 'string' ? t(loginError) : JSON.stringify(loginError)}
              </div>
            )}

            <button 
              type="submit" 
              style={{
                ...buttonStyle,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: loading ? secondaryColor : primaryColor,
              }}
              disabled={loading}
              onMouseOver={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = primaryDarkColor;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = primaryColor;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {loading ? t('로그인 중...') : t('로그인')}
            </button>
            
            <a 
              href="/signup" 
              style={signupButtonStyle}
              onClick={(e) => {
                e.preventDefault();
                handleSignupClick();
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = primaryDarkColor;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
            >
              {t('회원가입')}
            </a>
          </form>
        )}
      </div>
    </div>
  );
};

export default BasicLoginSkin;